import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Form,
  Input,
  InputNumber,
  Switch,
  Typography,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { getConfigDev, updateConfigDev } from "../services/siteConfig.service";
import { swalNotification } from "../components/alret/notificztion";
import Pageloader from "../components/loader";
import "./SiteConfigDev.css";

const { Title, Text } = Typography;
const { TextArea } = Input;

const humanizeKey = (key) =>
  String(key || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const isSecretKey = (key) => /token|auth|password|secret/i.test(key || "");

const isTrueFalseValue = (value) => {
  if (value === true || value === false) return true;
  if (value === 0 || value === 1) return true;
  if (value === "0" || value === "1") return true;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "true" || normalized === "false";
  }
  return false;
};

const classifyValue = (key, value) => {
  if (Array.isArray(value) || (value !== null && typeof value === "object")) {
    return "json";
  }
  if (isTrueFalseValue(value)) {
    if (value === true || value === false) return "boolean";
    if (typeof value === "string" && ["true", "false"].includes(value.trim().toLowerCase())) {
      return "flagBooleanString";
    }
    if (value === "0" || value === "1") return "flagString";
    return "flagNumber";
  }
  if (isSecretKey(key)) return "secret";
  if (typeof value === "number") return "number";
  if (typeof value === "string" && value.trim() !== "" && !Number.isNaN(Number(value))) {
    return "numberString";
  }
  return "text";
};

const toFormValue = (type, value) => {
  if (type === "json") {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return "";
    }
  }
  if (
    type === "boolean" ||
    type === "flagNumber" ||
    type === "flagString" ||
    type === "flagBooleanString"
  ) {
    return (
      value === true ||
      value === 1 ||
      value === "1" ||
      String(value).trim().toLowerCase() === "true"
    );
  }
  return value;
};

const fromFormValue = (type, value) => {
  if (type === "json") {
    const raw = typeof value === "string" ? value.trim() : "";
    if (!raw) return null;
    return JSON.parse(raw);
  }
  if (type === "boolean") return Boolean(value);
  if (type === "flagBooleanString") return value ? "true" : "false";
  if (type === "flagNumber") return value ? 1 : 0;
  if (type === "flagString") return value ? "1" : "0";
  if (type === "number") {
    return value === null || value === undefined || value === "" ? value : Number(value);
  }
  if (type === "numberString") {
    return value === null || value === undefined || value === "" ? value : String(value);
  }
  return value;
};

const isSwitchType = (type) =>
  type === "boolean" ||
  type === "flagNumber" ||
  type === "flagString" ||
  type === "flagBooleanString";

const ConfigControl = ({ fieldType, ...props }) => {
  if (isSwitchType(fieldType)) {
    return (
      <Switch
        checkedChildren="True"
        unCheckedChildren="False"
        {...props}
      />
    );
  }
  if (fieldType === "number" || fieldType === "numberString") {
    return <InputNumber className="config-dev-input" {...props} />;
  }
  if (fieldType === "json") {
    return <TextArea rows={8} className="config-dev-json" {...props} />;
  }
  if (fieldType === "secret") {
    return <Input.Password className="config-dev-input" {...props} />;
  }
  return <Input className="config-dev-input" {...props} />;
};

const ConfigTabForm = ({ type, items, loading, saving, onSave }) => {
  const [form] = Form.useForm();
  const [search, setSearch] = useState("");

  const meta = useMemo(() => {
    const next = {};
    (items || []).forEach((item) => {
      next[item.key] = classifyValue(item.key, item.value);
    });
    return next;
  }, [items]);

  useEffect(() => {
    const values = {};
    (items || []).forEach((item) => {
      values[item.key] = toFormValue(meta[item.key], item.value);
    });
    form.setFieldsValue(values);
  }, [form, items, meta]);

  const groupedItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filtered = (items || []).filter((item) => {
      if (!query) return true;
      const key = String(item.key || "").toLowerCase();
      const label = humanizeKey(item.key).toLowerCase();
      return key.includes(query) || label.includes(query);
    });

    return {
      switches: filtered.filter((item) => isSwitchType(meta[item.key])),
      values: filtered.filter(
        (item) => !isSwitchType(meta[item.key]) && meta[item.key] !== "json"
      ),
      json: filtered.filter((item) => meta[item.key] === "json"),
    };
  }, [items, meta, search]);

  const handleFinish = async (values) => {
    try {
      const payload = (items || []).map((item) => ({
        key: item.key,
        value: fromFormValue(meta[item.key], values[item.key]),
      }));
      await onSave(type, payload);
    } catch (error) {
      swalNotification(
        "Site Config Dev",
        error?.message || "Invalid JSON in one of the fields",
        "error"
      );
    }
  };

  const renderRow = (item, wide = false) => {
    const fieldType = meta[item.key];
    return (
      <div
        className={`config-dev-row${wide ? " config-dev-row-wide" : ""}`}
        key={item.key}
      >
        <div className="config-dev-label">
          <span className="config-dev-title">{humanizeKey(item.key)}</span>
          <Text className="config-dev-slug">{item.key}</Text>
        </div>
        <Form.Item
          name={item.key}
          valuePropName={isSwitchType(fieldType) ? "checked" : "value"}
          className="config-dev-control"
        >
          <ConfigControl fieldType={fieldType} />
        </Form.Item>
      </div>
    );
  };

  return (
    <Form form={form} layout="vertical" onFinish={handleFinish} className="config-dev-form">
      <div className="config-dev-toolbar">
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search configuration"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="config-dev-search"
        />
        <Button type="primary" htmlType="submit" loading={saving || loading}>
          Update {type === "web" ? "Web" : "APP"} Config
        </Button>
      </div>

      {!groupedItems.switches.length &&
      !groupedItems.values.length &&
      !groupedItems.json.length ? (
        <div className="config-dev-empty">No configuration found</div>
      ) : (
        <>
          {groupedItems.switches.length > 0 && (
            <section className="config-dev-section">
              <h3>Flags</h3>
              <div className="config-dev-grid">
                {groupedItems.switches.map((item) => renderRow(item))}
              </div>
            </section>
          )}
          {groupedItems.values.length > 0 && (
            <section className="config-dev-section">
              <h3>Values</h3>
              <div className="config-dev-grid">
                {groupedItems.values.map((item) => renderRow(item))}
              </div>
            </section>
          )}
          {groupedItems.json.length > 0 && (
            <section className="config-dev-section">
              <h3>JSON</h3>
              <div className="config-dev-json-list">
                {groupedItems.json.map((item) => renderRow(item, true))}
              </div>
            </section>
          )}
        </>
      )}
    </Form>
  );
};

const SiteConfigDev = () => {
  const [activeTab, setActiveTab] = useState("web");
  const [webItems, setWebItems] = useState([]);
  const [appItems, setAppItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadTab = async (type) => {
    try {
      setLoading(true);
      const resp = await getConfigDev(type);
      const data = Array.isArray(resp?.data?.data) ? resp.data.data : [];
      if (type === "web") setWebItems(data);
      else setAppItems(data);
    } catch (error) {
      swalNotification(
        "Site Config Dev",
        error?.response?.data?.message || "Failed to load config",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTab("web");
    loadTab("app");
  }, []);

  const handleSave = async (type, payload) => {
    try {
      setSaving(true);
      const response = await updateConfigDev(type, payload);
      if (response?.data && response?.data?.error === false) {
        swalNotification(
          "Site Config Dev",
          response?.data?.message || "Configuration updated successfully",
          "success"
        );
        await loadTab(type);
      }
    } catch (error) {
      swalNotification(
        "Site Config Dev",
        error?.response?.data?.message || "Something went wrong",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-content config-dev-page">
      <div className="page-content-header">
        <div className="pch">
          <Title level={2}>Site Config Dev</Title>
        </div>
      </div>
      <div className="p-content">
        <div className="config-dev-shell">
          <div className="config-dev-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "web"}
              className={activeTab === "web" ? "active" : ""}
              onClick={() => setActiveTab("web")}
            >
              Web
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "app"}
              className={activeTab === "app" ? "active" : ""}
              onClick={() => setActiveTab("app")}
            >
              APP
            </button>
          </div>

          {loading && !webItems.length && !appItems.length ? (
            <Pageloader />
          ) : (
            <div className="config-dev-panel">
              {activeTab === "web" ? (
                <ConfigTabForm
                  type="web"
                  items={webItems}
                  loading={loading}
                  saving={saving}
                  onSave={handleSave}
                />
              ) : (
                <ConfigTabForm
                  type="app"
                  items={appItems}
                  loading={loading}
                  saving={saving}
                  onSave={handleSave}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SiteConfigDev;
